import React, { useMemo, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import BaseTableCard, {
  buildTableCardItems,
} from 'components/atoms/base-table-card';
import { useRunbookLog } from 'infrastructure/hooks/clinic/use-runbook-logs';
import { DateFormatter } from 'infrastructure/functions';
import BaseSpinWrapper from 'components/atoms/base-spin-wrapper';
import classNames from 'classnames';
import RunBookCompareContent from 'components/organisms/runbooks/components/runbook-compare-content';
import BaseButton from 'components/atoms/baseButton';
import ComparerButton from 'components/atoms/icons/comparerButton';
import { DateFormats } from 'infrastructure/enums';

import s from './styles.module.scss';

const checkValue = (v?: string | null) => v || '-';

interface IProps {
  clinicGuid: string | undefined;
}

const ClinicInstructionsCard: React.FC<IProps> = (props) => {
  const { clinicGuid } = props;

  const [comparerOpened, toggleOpenComparer] = useReducer((v) => !v, false);
  const { t } = useTranslation();
  const { runbookLogs, loading } = useRunbookLog({
    meta: {
      clinicGuid,
      items: 100,
    },
  });

  const [instructionData, ...history] = runbookLogs?.items ?? [];

  const clinicInstructions = useMemo(() => {
    return buildTableCardItems([
      [
        {
          label: t('labels.criticalBusinessHours'),
          value: checkValue(instructionData?.criticalBusinessHours),
          multiply: true,
          originView: true,
          htmlView: true,
        },
        {
          label: t('labels.criticalAfterHours'),
          value: checkValue(instructionData?.criticalAfterHours),
          multiply: true,
          originView: true,
          htmlView: true,
        },
      ],
      [
        {
          label: t('labels.businessHours'),
          value: checkValue(instructionData?.businessHours),
        },
        {
          label: t('labels.afterHours'),
          value: checkValue(instructionData?.afterHours),
        },
        {
          label: t('labels.contactMethod'),
          value: checkValue(instructionData?.contactMethod),
        },
        {
          label: t('labels.contactMethodValue'),
          value: checkValue(instructionData?.contactMethodValue),
        },
        {
          label: t('labels.dateNote'),
          value: checkValue(instructionData?.dateNoteLogged),
        },
        {
          label: t('labels.note'),
          value: checkValue(instructionData?.notes),
          multiply: true,
          originView: true,
          htmlView: true,
        },
      ],
    ]);
  }, [instructionData]);

  const updateDateLabel = `${t('labels.updateDate')}: ${DateFormatter({
    date: instructionData?.createdAt,
    format: DateFormats['MM-dd-yyyy | h:mm a'],
  })}`;

  const controls = instructionData ? (
    <div className={classNames(s.controls)}>
      <span>{updateDateLabel}</span>
      <BaseButton
        type="secondary"
        className={s['comparer-button']}
        onClick={toggleOpenComparer}
        icon={<ComparerButton />}
      />
    </div>
  ) : null;

  return (
    <>
      <BaseSpinWrapper spinning={loading}>
        <BaseTableCard
          title={t('labels.clinicInstructions')}
          controls={controls}
          columns={2}
          rows={[1, 5]}
          items={clinicInstructions}
        />
      </BaseSpinWrapper>
      {comparerOpened && (
        <RunBookCompareContent
          comparerOpened
          setComparerOpened={toggleOpenComparer}
          currentData={instructionData}
          history={history}
        />
      )}
    </>
  );
};

export default ClinicInstructionsCard;
